import { sbEditable } from '@storyblok/storyblok-editable';
import classNames from 'classnames';
import gsap from 'gsap';
import type { ReactElement, RefObject, Ref } from 'react';
import { createRef, useEffect, useRef } from 'react';
import styles from './ModernCreativityInfo.module.scss';
import { setupSlidesTimeline, setupTransitionInTimeline } from './ModernCreativityInfo.transitions';
import type {
  ModernCreativityInfoProps,
  ModernCreativityInfoRef,
} from './ModernCreativityInfo.data';
import ModernCreativityInfoItem from './modern-creativity-info-item/ModernCreativityInfoItem';
import useScrollTrigger from '../../../hooks/useScrollTrigger';
import type { ModernCreativityInfoItemRef } from './modern-creativity-info-item/ModernCreativityInfoItem.data';
import useSetupTimeline from '../../../hooks/useSetupTimeline';
import LottieView from '../../atoms/lottie-view/LottieView';
import type { LottieViewRef } from '../../atoms/lottie-view/LottieView.data';
import useWindowSize from '../../../hooks/useWindowSize';

export default function ModernCreativityInfo({
  blok,
  ref,
  ...props
}: ModernCreativityInfoProps & { ref?: Ref<ModernCreativityInfoRef> }): ReactElement {
  const localRef = useRef(null);
  const elementRef = (ref || localRef) as RefObject<HTMLDivElement | null>;
  const containerRef = useRef<HTMLDivElement>(null);
  const currentSlideIndex = useRef<number | undefined>(undefined);
  const lottieRef = useRef<LottieViewRef>(null);
  const itemsRef = useRef(blok.items.map(() => createRef<ModernCreativityInfoItemRef>()));

  const contentRef = useRef<HTMLDivElement>(null);

  const slidesTimeline = useRef<gsap.core.Timeline>(undefined);
  slidesTimeline.current = useSetupTimeline(setupSlidesTimeline, {
    itemsRef,
  });

  const { width, height } = useWindowSize(0);

  useEffect(() => {
    const minHeight = Math.max(...itemsRef.current.map((item) => item?.current?.offsetHeight || 0));
    gsap.set(contentRef.current, { minHeight: `${minHeight}px` });
  }, [width, height, itemsRef]);

  useScrollTrigger({
    trigger: elementRef,
    animation: useSetupTimeline(setupTransitionInTimeline, {
      lottieRef,
      itemsRef,
    }),
    vars: {
      start: 'top center',
      end: 'bottom top-=250%',
      scrub: 1,
      onUpdate: ({ progress }) => {
        let index;

        if (progress <= 0) {
          index = -1;
        }

        if (progress > 0 && progress < 0.33) {
          index = 0;
        } else if (progress > 0.33 && progress < 0.66) {
          index = 1;
        } else if (progress > 0.66) {
          index = 2;
        }

        if (typeof index === 'number' && currentSlideIndex.current !== index) {
          // const position = slidesTimeline?.current.time();
          slidesTimeline?.current?.tweenTo(`slide_${index}`);
        }

        currentSlideIndex.current = index;
      },
    },
  });

  useScrollTrigger({
    trigger: elementRef,
    pin: containerRef,
    vars: {
      start: 'center center',
      end: 'bottom top-=250%',
      pinType: 'transform',
      anticipatePin: 1,
    },
  });

  return (
    <div
      ref={elementRef}
      className={classNames(
        styles.modernCreativityInfo,
        props.className,
        'grid-gutter max-content-width',
      )}
      {...sbEditable(blok)}
    >
      <div ref={containerRef} className={classNames(styles.container)}>
        <div className={classNames(styles.animationHolder)}>
          <div className={classNames(styles.animation)}>
            <LottieView
              ref={lottieRef}
              id="modern-creativity"
              loop={false}
              className={classNames(styles.symbol, 'abs-center')}
            />
          </div>
        </div>

        <div ref={contentRef} className={classNames(styles.content)}>
          {blok.items.map((item, index) => (
            <ModernCreativityInfoItem
              ref={itemsRef.current[index]}
              key={item._uid}
              blok={item}
              category={blok.title}
              link={blok.link[0]}
              className={classNames(styles.item)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
